import * as React from 'react'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import { Circle } from 'lucide-react'

import { cn } from 'app/lib/utils'
import { Label } from 'app/components/ui/label'

export type RadioGroupProps = {
  name?: string
  label?: string
  values: { value: string; label: string; description?: string }[]
  value?: string
  defaultValue?: string
  disabled?: boolean
  className?: string
  labelClassName?: string
  orientation?: `horizontal` | `vertical`
  onChange?: (value: string) => void
}

/**
 * Standalone RadioGroup component (shadcn/radix).
 * Drop-in replacement for FormRadioGroup without remix-validated-form dependency.
 *
 * @example
 * <RadioGroup
 *   label="Audio Type"
 *   values={[{ value: 'tts', label: 'Text-to-Speech' }, { value: 's3', label: 'Upload Audio File' }]}
 *   value={audioType}
 *   onChange={setAudioType}
 * />
 */
export const RadioGroup = ({
  name,
  label,
  values,
  value,
  defaultValue,
  disabled,
  className,
  labelClassName,
  orientation,
  onChange,
}: RadioGroupProps) => {
  const descriptionIdPrefix = React.useId()
  return (
    <div className={cn(`flex flex-col`, className)}>
      {label && <Label className={cn(`mb-1.5 text-sm font-medium`, labelClassName)}>{label}</Label>}
      <RadioGroupPrimitive.Root
        name={name}
        value={value}
        defaultValue={defaultValue}
        onValueChange={onChange}
        disabled={disabled}
        orientation={orientation}
        className={orientation === `vertical` ? `flex flex-col gap-3` : `flex items-center space-x-3`}
      >
        {values.map((v, index) => {
          const descriptionId = v.description ? `${descriptionIdPrefix}-${index}` : undefined
          return (
            <div key={v.value} className="flex flex-col gap-0.5">
              <label
                className={cn(
                  `flex cursor-pointer items-center space-x-1.5`,
                  disabled && `cursor-not-allowed opacity-50`,
                )}
              >
                <RadioGroupPrimitive.Item
                  value={v.value}
                  aria-describedby={descriptionId}
                  className="aspect-square h-4 w-4 rounded-full border border-strong text-blue-600 shadow-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-blue-600"
                >
                  <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
                    <Circle className="h-2.5 w-2.5 fill-current text-current" />
                  </RadioGroupPrimitive.Indicator>
                </RadioGroupPrimitive.Item>
                <span className="text-body-sm">{v.label}</span>
              </label>
              {v.description && (
                <p id={descriptionId} className="truncate pl-[22px] text-body-xs text-subtle">
                  {v.description}
                </p>
              )}
            </div>
          )
        })}
      </RadioGroupPrimitive.Root>
    </div>
  )
}
